// QuestionPanel — promoted from learner-app/components/session
// to @flowgenix/shared-ui (Phase 2 / Step C-3.3).
//
// Renderer for the `quiz_panel` widget kind. The original lived inside
// the learner-app session runtime and was tightly coupled to the Phaser
// scene via a singleton `EventBus`. To make the widget portable we
// accept an optional `eventBus` prop. When passed, tap-actor mode works
// exactly as before; when omitted, tap-actor questions gracefully
// degrade to "tap not available — submit empty".
//
// react-i18next is a static import. If no I18nextProvider is mounted,
// react-i18next returns a default `t` that echoes the key, which is
// acceptable for Atlas Catalog previews.
//
// Sessions running inside scenario-os-learner-app continue to pass
// `eventBus={EventBus}` from `learner-app/src/game/EventBus`.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export default function QuestionPanel({ question, onAnswer, timeLimit, eventBus }) {
  const { t } = useTranslation('session');
  const [selected, setSelected] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(Date.now());

  const isTapActor = question?.format === 'tap_actor';

  const handleSubmit = useCallback((choiceId) => {
    if (submitted) return;
    setSubmitted(true);
    const responseTimeMs = Date.now() - startRef.current;
    if (isTapActor && eventBus?.emit) {
      eventBus.emit('disable-tap-actor');
    }
    onAnswer({
      question_template_id: question.template_id,
      selected_choice_id: choiceId || selected,
      response_time_ms: responseTimeMs,
    });
  }, [submitted, selected, question, isTapActor, onAnswer, eventBus]);

  useEffect(() => {
    startRef.current = Date.now();
    setSelected(null);
    setSubmitted(false);
    setElapsed(0);
  }, [question?.template_id]);

  useEffect(() => {
    if (!isTapActor || submitted) return;
    if (!eventBus?.emit) return;

    const selectableActorIds = question?.selectable_actors || [];
    eventBus.emit('enable-tap-actor', {
      selectableActorIds,
      onSelect: (actorId) => {
        setSelected(actorId);
        handleSubmit(actorId);
      },
    });

    return () => {
      eventBus.emit('disable-tap-actor');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.template_id, isTapActor, submitted, eventBus]);

  useEffect(() => {
    if (submitted || !timeLimit) return;
    const interval = setInterval(() => {
      const ms = Date.now() - startRef.current;
      setElapsed(ms);
      if (ms >= timeLimit) {
        clearInterval(interval);
        handleSubmit(null);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [question?.template_id, submitted, timeLimit, handleSubmit]);

  if (!question) return null;

  const remaining = timeLimit ? Math.max(0, Math.ceil((timeLimit - elapsed) / 1000)) : null;

  return (
    <div className="question-panel">
      {remaining !== null && (
        <div className="timer-bar">
          <span>{t('time_remaining')}: {remaining}s</span>
          <div className="timer-fill" style={{ width: `${(1 - elapsed / timeLimit) * 100}%` }} />
        </div>
      )}

      <h3 className="question-prompt">{question.prompt}</h3>

      {isTapActor ? (
        <div className="tap-actor-instruction">
          <div className="tap-actor-icon">👆</div>
          <p>{t('tap_player_on_field', { ns: 'session', defaultValue: 'Tap the correct player on the field' })}</p>
          {!eventBus?.emit && (
            <p className="tap-actor-warning">{t('tap_actor_no_bus', { defaultValue: '(Tap target not available in this context)' })}</p>
          )}
          {submitted && selected && (
            <p className="tap-actor-selected">
              {t('selected', { ns: 'session', defaultValue: 'Selected' })}: <strong>{selected}</strong>
            </p>
          )}
        </div>
      ) : (
        <>
          <div className="choices">
            {question.choices?.map((choice) => (
              <button
                key={choice.choice_id}
                className={`choice-btn ${selected === choice.choice_id ? 'selected' : ''}`}
                onClick={() => !submitted && setSelected(choice.choice_id)}
                disabled={submitted}
              >
                {choice.text}
              </button>
            ))}
          </div>

          {!submitted && selected && (
            <button className="btn-primary" onClick={() => handleSubmit(selected)}>
              {t('answer_question', { ns: 'session' })}
            </button>
          )}
        </>
      )}
    </div>
  );
}

QuestionPanel.kind = 'quiz_panel';
QuestionPanel.displayName = 'QuestionPanel';
